import { Badge, Flex, Text } from '@chakra-ui/react';

import { CellContext, ColumnDef, createColumnHelper } from '@tanstack/react-table';

import {
  Badge as CommonBadge,
  colors,
  EditableCell,
  PayCycleStatus,
  PayFrequency,
  safeStringFormatDate,
} from '@aslanpay/portal-common-lib';

import {
  getPayCycleStageLabel,
  PAY_CYCLE_STAGE_BADGES,
} from 'app/components/PayCalendars/PayCycleSummary';
import CircleWithInitials from 'app/components/UI/CircleWithInitials';
import { PayCycleRow } from 'app/components/UI/Table/Table.types';

import PayCycleStatusValues from 'app/constants/payCycles';
import { PayCycleField } from 'app/constants/table';

import { getPayCycleStage } from 'app/utils/payCycles';

function renderEditableDateCell(info: CellContext<PayCycleRow, unknown>, formatString?: string) {
  const tableMeta = info.table.options.meta;
  const isEditing = tableMeta?.editingColumns?.includes(info.column.id);
  const initialValue = (info.getValue() ?? '') as string;

  if (isEditing) {
    return (
      <EditableCell
        initialValue={initialValue}
        type="date"
        disablePast
        onSave={(newValue) => tableMeta?.updateData(info.row.index, info.column.id, newValue)}
      />
    );
  }
  return <Text>{safeStringFormatDate(initialValue, { formatString })}</Text>;
}

function renderDayWarning(text: string) {
  return (
    <CircleWithInitials
      bgColor="system.warning"
      size="20px"
      color="brand.black"
      textStyle="extraExtraExtraSmallSemiBold"
      text={text}
    />
  );
}

// isReadOnly locks cut-off and payday editing, for an archived calendar whose final cut-off must not move.
function getPayCyclesColumns(payFrequency?: PayFrequency, isReadOnly = false) {
  const columnHelper = createColumnHelper<PayCycleRow>();

  const fields = Object.keys(PayCycleField) as Array<keyof PayCycleRow>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns: ColumnDef<PayCycleRow, any>[] = fields.map((field) => {
    return columnHelper.accessor(field as keyof PayCycleRow, {
      cell: (info) => {
        switch (field as keyof typeof PayCycleField) {
          case 'period':
            return payFrequency === 'MONTHLY'
              ? safeStringFormatDate(info.row.original.startDate as string, {
                  formatString: 'MMMM',
                })
              : info.getValue();
          case 'startDate':
          case 'endDate':
          case 'accrualStartDate':
          case 'accrualEndDate':
            return safeStringFormatDate(info.getValue() as string);
          case 'payday':
            return (
              <Flex gap="10px" alignItems="center">
                {renderEditableDateCell(info)}
                {info.row.original.isPaydayHoliday && renderDayWarning('Public Holiday')}
                {info.row.original.isPaydayWeekend && renderDayWarning('Weekend')}
              </Flex>
            );
          case 'cutoffDate':
            return (
              <Flex gap="10px" alignItems="center">
                {renderEditableDateCell(info)}
                {info.row.original.isCutoffHoliday && renderDayWarning('Public Holiday')}
                {info.row.original.isCutoffWeekend && renderDayWarning('Weekend')}
              </Flex>
            );
          case 'odpAllowancePercentage':
            return `${info.getValue()}%`;
          case 'status': {
            const stage = getPayCycleStage(info.row.original);

            if (!stage) {
              return (
                <Badge
                  colorScheme={`badge.${
                    PayCycleStatusValues[info.getValue() as PayCycleStatus]?.color
                  }`}
                >
                  {PayCycleStatusValues[info.getValue() as PayCycleStatus]?.value}
                </Badge>
              );
            }

            return (
              <CommonBadge
                value={getPayCycleStageLabel(stage, info.row.original.payday) ?? ''}
                status={PAY_CYCLE_STAGE_BADGES[stage]}
              />
            );
          }
          default:
            return info.getValue();
        }
      },
      header: PayCycleField[field as keyof typeof PayCycleField],
      meta: {
        isEditable: !isReadOnly && (field === 'cutoffDate' || field === 'payday'),
        getCellContext: (context: CellContext<PayCycleRow, unknown>) => {
          const columnId = context.column.id;
          const tableMeta = context.table.options.meta;

          const isEditableColumn =
            !isReadOnly && (columnId === 'cutoffDate' || columnId === 'payday');
          const isCompleted = context.row.original.status === 'COMPLETED';

          const isWarning =
            (columnId === 'cutoffDate' &&
              (context.row.original.isCutoffHoliday || context.row.original.isCutoffWeekend)) ||
            (columnId === 'payday' &&
              (context.row.original.isPaydayHoliday || context.row.original.isPaydayWeekend));

          return {
            style: {
              ...(isWarning && { backgroundColor: colors.alert.warning }),
              ...(isEditableColumn && !isCompleted && { cursor: 'pointer' }),
            },
            onClick: () => {
              if (isEditableColumn && !isCompleted) {
                if (!tableMeta?.editingColumns?.includes(columnId)) {
                  tableMeta?.setEditingColumn(columnId);
                }
              }
            },
          };
        },
      },
    });
  });

  return columns;
}

export default getPayCyclesColumns;
